import { useLoaderData, useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { repeatCashEntryText } from "@/utils/billingPeriod.helpers";
import {
  getMemberships,
  getPlatform,
  getReferralRewardsHistory,
  getRepeatCashTransactions,
  getUser,
} from "api/methods";
import { Models } from "api/types";
import { RepeatCashTransactionList } from "organisms/repeatcash-transaction-list/RepeatCashTransactionList";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { waitObj } from "toolbox/Promise";
import { defineLoader } from "toolbox/Router";

import { WalletRepeatCashCard } from "./RepeatCashCard";

type RepeatCashTransactionModel = Models<"DisplayRepeatCashTransactionForPerson">;

// Maps to backend RepeatCashActivityType enum
const RepeatCashActivityType = {
  AddedByStaff: 0,
  GiftedByStaff: 1,
  AddedByMembership: 2,
  BankedReward: 3,
  Purchase: 4,
  Refund: 5,
  DeductedByStaff: 6,
  Unknown: 7,
  GiftCard: 8,
  ReferralReward: 9,
  ReferralRewardBonus: 10,
} as const;

const getTransactionTitle = (
  transaction: RepeatCashTransactionModel,
  memberships: Models<"MembershipModel">[],
  isFlexibleMembershipsEnabled: boolean,
  t: ReturnType<typeof useTranslation>["t"],
) => {
  if (transaction.activityType === RepeatCashActivityType.BankedReward) {
    return transaction.activityDescription || "";
  }
  if (transaction.activityType === RepeatCashActivityType.Purchase) {
    return transaction.activityDescription || "";
  }
  if (transaction.activityType === RepeatCashActivityType.AddedByMembership) {
    const relatedMembership = memberships.find(
      (membership) => membership.id === transaction.membershipId,
    );
    const intervalText = repeatCashEntryText(relatedMembership);
    const membershipName =
      relatedMembership?.name || t("walletRepeatCashPage.membershipDefaultName");
    return isFlexibleMembershipsEnabled
      ? t("walletRepeatCashPage.membershipCredit", { membershipName, intervalText })
      : t("walletRepeatCashPage.monthlyMembershipCash", { membershipName });
  }
  if (
    transaction.activityType === RepeatCashActivityType.ReferralReward ||
    transaction.activityType === RepeatCashActivityType.ReferralRewardBonus
  ) {
    return t("walletRepeatCashPage.referralReward");
  }
  if (transaction.activityType === RepeatCashActivityType.GiftCard) {
    return t("walletRepeatCashPage.repeatCashAdded");
  }
  if (transaction.transactionType === "CashAdded") {
    return t("walletRepeatCashPage.repeatCashAdded");
  }
  if (transaction.transactionType === "CashWithdrawal") {
    return t("walletRepeatCashPage.repeatCashDeducted");
  }
  if (transaction.reason) {
    return t("walletRepeatCashPage.repeatCashUsedInOffice");
  }

  return transaction.activityTitle || t("walletRepeatCashPage.unknown");
};

type LoaderData = Awaited<ReturnType<typeof walletRepeatCashPageLoader>>;
export const walletRepeatCashPageLoader = defineLoader(async () => {
  const userInfo = queryClient.fetchQuery({
    ...getUser.keyed(),
    staleTime: 0,
  });

  const repeatCashTransactions = queryClient.fetchQuery({ ...getRepeatCashTransactions.keyed() });

  const platformMemberships = queryClient.fetchQuery({ ...getMemberships.keyed() });

  const platform = queryClient.fetchQuery({ ...getPlatform.keyed() });

  // Get user data first to access platform ID
  const user = await userInfo;

  const referralRewardsHistory = user?.platform?.id
    ? queryClient.fetchQuery({
        ...getReferralRewardsHistory.keyed({ platformId: user.platform!.id }),
      })
    : Promise.resolve(null);

  return waitObj({
    userInfo,
    repeatCashTransactions,
    platformMemberships,
    platform,
    referralRewardsHistory,
  });
});

export const WalletRepeatCashPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    userInfo,
    repeatCashTransactions,
    platformMemberships,
    platform,
    referralRewardsHistory,
  } = useLoaderData() as LoaderData;
  const isFlexibleMembershipsEnabled = useFeatureFlag("flexible_membership_terms");
  const platformHasBeautyBank = platformMemberships.some((membership) =>
    membership.membershipRewardGroups?.some((rg) => rg.beautyBank?.creditAmount !== null),
  );
  const shouldShowRewards = platform?.platformSettings?.rewardsEnabled;
  const checkInDisabled = platform?.platformSettings?.patientCheckInDisabled ?? false;

  const completedReferralUsers = (
    referralRewardsHistory?.referredUsers?.filter(
      (user) => user.status === "Completed" && user.referrerReward.type === "RepeatCash",
    ) || []
  ).sort((a, b) => {
    const dateA = new Date(a.referrerReward.obtainedAt || a.dateJoined);
    const dateB = new Date(b.referrerReward.obtainedAt || b.dateJoined);
    return dateB.getTime() - dateA.getTime();
  });

  const availableReferralUsers = [...completedReferralUsers];

  const findReferralUserForTransaction = (transaction: RepeatCashTransactionModel) => {
    if (
      transaction.activityType !== RepeatCashActivityType.ReferralReward &&
      transaction.activityType !== RepeatCashActivityType.ReferralRewardBonus
    ) {
      return null;
    }

    const userIndex = availableReferralUsers.findIndex((user) => {
      const rewardDate = user.referrerReward.obtainedAt || user.dateJoined;
      const transactionDate = new Date(transaction.created || 0);
      const userRewardDate = new Date(rewardDate);

      const amountMatches = Math.abs(user.referrerReward.amount - transaction.amountInCents) < 0.01;
      const datesDifferenceInDays =
        Math.abs(transactionDate.getTime() - userRewardDate.getTime()) / (1000 * 60 * 60 * 24);
      const datesAreClose = datesDifferenceInDays <= 7;

      return amountMatches && datesAreClose;
    });

    if (userIndex !== -1) {
      return availableReferralUsers.splice(userIndex, 1)[0];
    }

    return null;
  };

  const repeatCashTransactionList: RepeatCashTransactionModel[] =
    repeatCashTransactions?.transactions ?? [];

  const allTransactions = repeatCashTransactionList
    .map((transaction) => {
      const referralUser = findReferralUserForTransaction(transaction);
      if (referralUser) {
        const firstNameWithLastInitial = `${
          referralUser.firstName
        } ${referralUser.lastName.charAt(0)}.`;
        return {
          ...transaction,
          activityTitle: t("walletRepeatCashPage.referralCheckIn", {
            name: firstNameWithLastInitial,
          }),
        };
      }

      return {
        ...transaction,
        activityTitle: getTransactionTitle(
          transaction,
          platformMemberships,
          isFlexibleMembershipsEnabled,
          t,
        ),
      };
    })
    .sort((a, b) => new Date(b.created || 0).getTime() - new Date(a.created || 0).getTime());

  const renderBeautyBankMessage = () => {
    // Both default strings tell patients to check in, which practice-managed platforms cannot do.
    let message = checkInDisabled
      ? t("walletRepeatCashPage.earnRepeatCashRewardsNoCheckIn")
      : t("walletRepeatCashPage.earnRepeatCashRewards");

    if (!shouldShowRewards) {
      message = t("walletRepeatCashPage.applyRepeatCash");
    }

    if (platformHasBeautyBank) {
      message = checkInDisabled
        ? t("walletRepeatCashPage.earnRepeatCashBeautyBankNoCheckIn")
        : t("walletRepeatCashPage.earnRepeatCashBeautyBank");
    }

    return <div className="text-center text-body1 text-secondary">{message}</div>;
  };

  return (
    <div className="flex h-full flex-col gap-10 py-8">
      <div className="flex flex-col gap-4 px-8 desk:max-w-md">
        <WalletRepeatCashCard
          amount={userInfo.repeatCashBalance}
          onClick={() => navigate("/shop")}
        />
        {renderBeautyBankMessage()}
      </div>
      <RepeatCashTransactionList transactions={allTransactions} />
    </div>
  );
};
